import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { currentUserId, sameUser } from "../api/identity";
import { CabinetProvider, useCabinet } from "../context/CabinetContext";
import CabinetLayout from "../components/cabinet/CabinetLayout";
import GameCardMini from "../components/cabinet/GameCardMini";
import CabIcon from "../components/cabinet/CabIcon";
import ProfileScreen from "../components/cabinet/screens/ProfileScreen";
import { AccountSub } from "../components/cabinet/screens/MoreScreen";
import {
  getUserById, getUserExtras,
  getProjectsByAuthor, getUserPlayHistory,
  getRubloxAchievs, putProfileLayout,
  getRubloxMedals, getRubloxStats,
  getBadges, putDisplayedBadges,
  getProfileComments, postProfileComment, deleteMyProfileComment,
  getProfileJudgement, putProfileJudgement,
  profilePhotoSrc,
  uploadProfileCover, deleteProfileCover, putCoverSettings,
  uploadProfileBackground, deleteProfileBackground, putBackgroundSettings,
  getGiftsCatalog, sendGift, getGiftShelf,
  getMyDiamonds,
} from "../api/cabinet";
import AvatarFrame from "../components/cabinet/AvatarFrame";
import GiftIcon from "../components/cabinet/GiftIcon";
import AchievIcon from "../components/cabinet/AchievIcon";
import MedalIcon from "../components/cabinet/MedalIcon";
import { RichAbout, AboutEditorModal } from "../components/cabinet/AboutEditor";
import { avatarInitialColor, formatCompact } from "../components/cabinet/cabinetUtils";
import usePageTitle from "../hooks/usePageTitle";
import "../styles/cabinet-feed.css";
import "../styles/cabinet-feed-extras.css";
import "../styles/cabinet-profile.css";
import "../styles/user-profile-page.css";

const TABS = [
  { id: "feed", label: "Главная", icon: "home" },
  { id: "top", label: "Топ", icon: "trophy" },
  { id: "avatar", label: "Аватар", icon: "userCircle" },
  { id: "friends", label: "Друзья", icon: "users" },
  { id: "more", label: "Ещё", icon: "more" },
];

/**
 * UserProfilePage — публичная страница профиля пользователя.
 * URL: /app/profile/:userId
 *
 * Дизайн: повторяет /me из minecraftia (медали, ачивки, комменты, рейтинг,
 * алмазы) но в светлой теме кабинета Рублокса (CabinetLayout с сайдбаром).
 * Дополнительно: созданные плейсы автора и история его последних игр.
 */
function UserProfileInner() {
  usePageTitle("Профиль пользователя");
  const { userId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, initializing, user: me } = useAuth();
  // Звёзды и кошелёк — из контекста кабинета (звёзды приватные, только свои)
  const { stars: myStars, openWallet } = useCabinet();

  // activeView — какую вкладку показываем: 'profile' / 'settings' / 'security'.
  // Видно только при isMe (на своей странице). На чужой странице всегда 'profile'.
  // Источник истины — query-параметр ?view=... (чтобы кнопки «Назад» работали).
  const viewParam = searchParams.get("view");
  const activeView = viewParam === "settings" ? "settings"
                   : viewParam === "security" ? "security"
                   : "profile";
  const switchView = useCallback((v) => {
    if (v === "profile") setSearchParams({}, { replace: false });
    else setSearchParams({ view: v }, { replace: false });
  }, [setSearchParams]);

  const [profile, setProfile] = useState(undefined);
  const [extras, setExtras] = useState(null); // {rating, diamond, idRecords}
  const [games, setGames] = useState(null);
  const [history, setHistory] = useState(null);
  // Достижения Рублокса (свои, не минкрафтийские). Прогресс считает бэк.
  const [achievs, setAchievs] = useState(null); // {achievs:[...], receivedCount, total}
  const [achievModal, setAchievModal] = useState(null); // ачивка для модалки прогресса
  // Медали Рублокса — зарабатываются (лидерборды/хиты/запуски), бэк фиксирует.
  const [medals, setMedals] = useState(null); // {medals:[...], receivedCount, total}
  const [medalModal, setMedalModal] = useState(null); // медаль для модалки
  const [widgetStats, setWidgetStats] = useState(null); // значения стат-виджетов шапки
  const [badgesModal, setBadgesModal] = useState(false); // модалка «Бейджи»
  const [layoutModal, setLayoutModal] = useState(false); // модалка «Оформление»
  // Живой предпросмотр оформления: пока LayoutModal открыта, черновик
  // применяется к странице сразу; закрыл без сохранения — сброс.
  const [layoutPreview, setLayoutPreview] = useState(null);
  const [comments, setComments] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [commentBusy, setCommentBusy] = useState(false);
  const [judgement, setJudgement] = useState(null); // {plus, minus, myChoice}
  const [error, setError] = useState(null);
  const [coverModal, setCoverModal] = useState(false); // модалка «Изменить обложку»
  const [bgModal, setBgModal] = useState(false); // модалка «Фон страницы»
  // Живой предпросмотр настроек фона/обложки: пока модалка открыта, черновик
  // настроек применяется к странице сразу; закрыл без сохранения — сброс.
  const [bgPreview, setBgPreview] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [aboutModal, setAboutModal] = useState(false); // модалка «О себе»
  const [giftModal, setGiftModal] = useState(false); // модалка «Подарить» (на чужой странице)
  const [giftShelf, setGiftShelf] = useState(null); // {gifts, count} — полка подарков

  /* ── редирект для гостей ──────────────────────────────────────── */
  useEffect(() => {
    if (initializing) return;
    if (!isAuthenticated) {
      navigate(`/login?next=/app/profile/${userId}`, { replace: true });
    }
  }, [initializing, isAuthenticated, navigate, userId]);

  /* ── загрузка профиля ─────────────────────────────────────────── */
  useEffect(() => {
    if (initializing || !isAuthenticated) return;
    const ctrl = new AbortController();
    let cancelled = false;

    (async () => {
      try {
        const u = await getUserById(userId, ctrl.signal);
        if (cancelled) return;
        if (!u) { setProfile(null); return; }
        setProfile(u);
      } catch (e) {
        if (e.name === "AbortError") return;
        setError("Не удалось загрузить профиль");
        setProfile(null);
      }
    })();

    return () => { cancelled = true; ctrl.abort(); };
  }, [userId, isAuthenticated, initializing]);

  /* ── остальные данные параллельно ─────────────────────────────── */
  useEffect(() => {
    if (initializing || !isAuthenticated || !profile) return;
    const ctrl = new AbortController();

    getUserExtras(userId, ctrl.signal).then(setExtras).catch(() => setExtras({}));
    getProjectsByAuthor(userId, 100, ctrl.signal).then(setGames).catch(() => setGames([]));
    getUserPlayHistory(userId, 5, ctrl.signal).then(setHistory).catch(() => setHistory([]));
    getProfileComments(userId, ctrl.signal).then(setComments).catch(() => setComments([]));
    getProfileJudgement(userId, ctrl.signal).then(setJudgement).catch(() => setJudgement({}));
    getGiftShelf(userId, ctrl.signal).then(setGiftShelf).catch(() => setGiftShelf({ gifts: [], count: 0 }));

    return () => ctrl.abort();
  }, [userId, isAuthenticated, initializing, profile]);

  /* ── достижения и медали Рублокса (прогресс уже посчитан бэком) ── */
  useEffect(() => {
    if (!profile) return;
    const ctrl = new AbortController();
    getRubloxAchievs(userId, ctrl.signal)
      .then(setAchievs)
      .catch(() => setAchievs({ achievs: [], receivedCount: 0, total: 0 }));
    // Медали Рублокса (замена минкрафтийских, убранных 2026-07-09):
    // зарабатываются за лидерборды/хиты недели/запуски, бэк фиксирует навсегда.
    getRubloxMedals(userId, ctrl.signal)
      .then(setMedals)
      .catch(() => setMedals({ medals: [], receivedCount: 0, total: 0 }));
    return () => ctrl.abort();
  }, [profile, userId]);

  /* ── оформление профиля (тема, порядок, панели, заголовки, виджеты).
   *    Пока открыта модалка «Оформление» — показываем её черновик. ── */
  const layout = layoutPreview || parseLayout(profile?.profile_layout);

  /* ── значения стат-виджетов шапки (если владелец их выбрал) ────── */
  const wantWidgets = layout.widgets.length > 0;
  useEffect(() => {
    if (!profile || !wantWidgets || widgetStats) return;
    const ctrl = new AbortController();
    getRubloxStats(userId, ctrl.signal)
      .then((s) => setWidgetStats(s || {}))
      .catch(() => setWidgetStats({}));
    return () => ctrl.abort();
  }, [profile, wantWidgets, widgetStats, userId]);

  /* ── заголовок секции: свой (Б5) или дефолтный ─────────────────── */
  const sectionTitle = (key) => layout.titles[key] || SECTION_TITLES[key];

  /* ── фон и обложка: картинка + настройки. Превью из модалки имеет
   *    приоритет — в т.ч. локальный файл ДО покупки (посмотреть и
   *    подогнать крутилки перед тратой алмазов). ─────────────────── */
  const bgUrl = bgPreview?.url
    || (profile?.backgroundPhoto ? profilePhotoSrc(profile.backgroundPhoto) : null);
  const bgSet = bgPreview?.s
    || parseImgSettings(profile?.background_settings, BG_DEFAULTS);
  const coverUrl = coverPreview?.url
    || (profile?.coverPhoto ? profilePhotoSrc(profile.coverPhoto) : null);
  const coverSet = coverPreview?.s
    || parseImgSettings(profile?.cover_settings, COVER_DEFAULTS);

  // isMe — открыта МОЯ страница. Сравниваем тремя парами, потому что
  // на разных страницах кабинета user.userId был то числом, то строкой,
  // и старая запись в localStorage может содержать stringified число.
  // JWT — наиболее надёжный источник: он выдан бэком при login.
  //
  // Сравнение через sameUser (строковое), а НЕ через Number(): при
  // UUID-идентификаторах v2 приведение к числу давало бы NaN, isMe стал
  // бы false — и владелец потерял бы управление собственным профилем,
  // без единой ошибки в консоли.
  const myIdFromJwt = currentUserId(
    (
      localStorage.getItem("rublox_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("Authorization") ||
      ""
    ).replace(/^Bearer\s+/i, "") || null
  );

  const isMe = !!(
    sameUser(myIdFromJwt, profile?.id) ||
    sameUser(me?.userId, userId) ||
    sameUser(me?.userId, profile?.id)
  );


  /* ── работа с комментариями ───────────────────────────────────── */
  const submitComment = useCallback(async () => {
    const text = commentText.trim();
    if (!text || commentBusy) return;
    setCommentBusy(true);
    try {
      const res = await postProfileComment(Number(userId), text);
      // Бэк отдаёт HTTP 200 даже на отказы — проверяем message.
      // Возможные отказы: "Слишком часто!", "Такой комментарий уже есть",
      // "email_not_confirmed" (403, ловится в catch ниже).
      const msg = (res?.message || "").toString();
      const succeeded = /добавлен|created|success|ok/i.test(msg) || !msg;
      if (!succeeded) {
        alert(msg);
      } else {
        setCommentText("");
      }
      // В любом случае подгрузим свежий список (если коммент сохранился — увидим)
      const fresh = await getProfileComments(userId);
      setComments(fresh);
    } catch (e) {
      const msg = e?.message || "Не удалось отправить";
      // 403 email_not_confirmed — подсказываем подтвердить email
      if (/email.?not.?confirmed/i.test(msg)) {
        alert("Подтвердите email через Яндекс или Google в кабинете, чтобы оставлять комментарии.");
      } else {
        alert(msg);
      }
    } finally {
      setCommentBusy(false);
    }
  }, [commentText, commentBusy, userId]);

  const removeComment = useCallback(async (cid) => {
    try {
      await deleteMyProfileComment(cid);
      setComments((prev) => (prev || []).filter((c) => c.id !== cid));
    } catch (e) {
      const msg = (e?.message || "").toString();
      // 403 — нет прав. Показываем понятную причину.
      if (/403|forbidden|можно удалять только/i.test(msg)) {
        alert("Можно удалять только комментарии на своей стене или свои собственные.");
      } else {
        alert(msg || "Не удалось удалить");
      }
    }
  }, []);

  const voteProfile = useCallback(async (change) => {
    try {
      await putProfileJudgement(Number(userId), change);
      const fresh = await getProfileJudgement(userId);
      setJudgement(fresh);
    } catch (e) {
      alert(e?.message || "Не удалось проголосовать");
    }
  }, [userId]);

  /* ── скин-аватар для сайдбара ─────────────────────────────────
   * Каталог скинов грузится async, для простоты UserProfilePage
   * показываем CabinetLayout без скин-аватара. Сам кабинет на /app
   * подгружает скин корректно (там есть useEffect для async). */
  const skinAvatar = "";

  /* ── рендер ───────────────────────────────────────────────────── */
  return (
    <CabinetLayout tabs={TABS} activeTab="feed" onTabChange={(id) => navigate(`/app${id === 'feed' ? '' : '?tab=' + id}`)}
      skinAvatar={skinAvatar}>
      {profile === undefined && (
        <div className="rb-feed__loading"><div className="rb-cab-spinner" /></div>
      )}

      {profile === null && (
        <div className="rb-cab-empty" style={{ marginTop: 60, textAlign: "center" }}>
          <div className="rb-cab-empty__text" style={{ fontSize: 18, marginBottom: 16 }}>
            {error || "Пользователь не найден"}
          </div>
          <Link to="/app" className="rb-btn rb-btn--primary"
                style={{ display: "inline-block", padding: "10px 22px", textDecoration: "none" }}>
            На главную
          </Link>
        </div>
      )}

      {profile && (
        <div
          className={[
            "rb-up",
            bgUrl ? "has-bg" : "",
            layout.panel.border !== "none" ? `rb-up--pb-${layout.panel.border}` : "",
            layout.titleFont !== "default" ? `rb-up--tf-${layout.titleFont}` : "",
          ].filter(Boolean).join(" ")}
          style={panelStyleVars(layout.panel)}
        >
          {/* ── ФОН СТРАНИЦЫ (платная кастомизация, 80 алмазов) ──
              Фиксированный слой на весь вьюпорт позади контента (как фоны
              профилей Steam). Отображение управляется настройками владельца
              (режим/позиция/масштаб/плитка/затемнение/размытие), вуаль — в
              ::after, секции поверх — полупрозрачные панели (.has-bg).
              В режимах height/width края заполняет размытая копия картинки
              (слой --fill), если владелец не выбрал «тёмные края». */}
          {bgUrl && (
            <>
              {(bgSet.mode === "height" || bgSet.mode === "width")
                && bgSet.fill !== "dark" && (
                <div className="rb-up__bg rb-up__bg--fill" style={imgFillStyle(bgUrl)} />
              )}
              <div className="rb-up__bg" style={imgLayerStyle(bgUrl, bgSet)} />
            </>
          )}

          {/* ── ШАПКА ПРОФИЛЯ ──────────────────────────────────── */}
          <ProfileHeader
            profile={profile}
            extras={extras}
            isMe={isMe}
            judgement={judgement}
            onVote={voteProfile}
            navigate={navigate}
            onOpenSettings={() => switchView("settings")}
            onEditCover={() => setCoverModal(true)}
            onEditBackground={() => setBgModal(true)}
            /* Была модалка-дубль магазина (рамки + цвет имени) со своей
               загрузкой каталогов и своими кнопками покупки. Тот же магазин
               уже есть в меню кабинета и умеет больше (ещё и темы), поэтому
               ведём туда: две копии одной витрины неизбежно расходятся —
               ровно это и произошло с темами, которые продавались в двух
               местах, пока дубль не убрали. */
            onOpenShop={() => navigate("/app/shop")}
            onOpenLayout={() => setLayoutModal(true)}
            onOpenBadges={() => setBadgesModal(true)}
            widgetKeys={layout.widgets}
            widgetStats={widgetStats}
            headerTheme={layout.theme}
            myStars={myStars}
            onOpenWallet={openWallet}
            cover={coverUrl ? { url: coverUrl, s: coverSet } : null}
          />

          {/* ── МОДАЛКА «ОБЛОЖКА ПРОФИЛЯ» (только для себя) ───────
              Те же крутилки, что у фона, живой предпросмотр в шапке. */}
          {isMe && coverModal && (
            <ImageSettingsModal
              title="Обложка профиля"
              icon="image"
              price={COVER_PRICE}
              defaults={COVER_DEFAULTS}
              hintNoImage={"Картинка появится в шапке твоего профиля вместо синего градиента. "
                           + "Подойдёт JPG, PNG или GIF — анимированные гифки будут двигаться. "
                           + "Выбери файл — сразу увидишь предпросмотр в шапке и подгонишь настройки ещё ДО покупки."}
              hintHasImage={"Настрой отображение обложки — изменения сразу видны в шапке позади. "
                            + "Можно загрузить и GIF — анимация сохранится. Не забудь нажать «Сохранить»."}
              removeLabel="Убрать обложку"
              hasImage={!!profile.coverPhoto}
              current={parseImgSettings(profile.cover_settings, COVER_DEFAULTS)}
              diamonds={extras?.diamond ?? null}
              doUpload={uploadProfileCover}
              doRemove={deleteProfileCover}
              doSaveSettings={putCoverSettings}
              resultKey="coverPhoto"
              settingsKey="cover_settings"
              onPreview={setCoverPreview}
              onClose={() => {
                setCoverPreview(null);
                setCoverModal(false);
              }}
              onUploaded={(coverPhoto, diamonds) => {
                setProfile((p) => ({ ...p, coverPhoto }));
                if (typeof diamonds === "number") {
                  setExtras((e) => (e ? { ...e, diamond: diamonds } : e));
                }
              }}
              onSavedSettings={(settingsStr) => {
                setProfile((p) => ({ ...p, cover_settings: settingsStr }));
                setCoverPreview(null);
                setCoverModal(false);
              }}
              onRemoved={() => {
                setProfile((p) => ({ ...p, coverPhoto: null, cover_settings: null }));
                setCoverPreview(null);
                setCoverModal(false);
              }}
            />
          )}

          {/* ── МОДАЛКА «ФОН СТРАНИЦЫ» (только для себя) ──────────
              Загрузка картинки (80💎) + бесплатные «крутилки» отображения
              с живым предпросмотром прямо на странице позади модалки. */}
          {isMe && bgModal && (
            <ImageSettingsModal
              title="Фон страницы"
              icon="wallpaper"
              price={BACKGROUND_PRICE}
              defaults={BG_DEFAULTS}
              hintNoImage={"Картинка станет фоном всей страницы твоего профиля — её увидят все гости. "
                           + "Подойдёт JPG, PNG или GIF — анимированные гифки будут двигаться. "
                           + "Выбери файл — сразу увидишь предпросмотр на странице и подгонишь настройки ещё ДО покупки."}
              hintHasImage={"Настрой отображение фона — изменения сразу видны на странице позади. "
                            + "Можно загрузить и GIF — анимация сохранится. Не забудь нажать «Сохранить»."}
              removeLabel="Убрать фон"
              hasImage={!!profile.backgroundPhoto}
              current={parseImgSettings(profile.background_settings, BG_DEFAULTS)}
              diamonds={extras?.diamond ?? null}
              doUpload={uploadProfileBackground}
              doRemove={deleteProfileBackground}
              doSaveSettings={putBackgroundSettings}
              resultKey="backgroundPhoto"
              settingsKey="background_settings"
              onPreview={setBgPreview}
              onClose={() => {
                setBgPreview(null);
                setBgModal(false);
              }}
              onUploaded={(backgroundPhoto, diamonds) => {
                // Модалку НЕ закрываем: сразу после покупки логично
                // покрутить настройки отображения (секция появится ниже).
                setProfile((p) => ({ ...p, backgroundPhoto }));
                if (typeof diamonds === "number") {
                  setExtras((e) => (e ? { ...e, diamond: diamonds } : e));
                }
              }}
              onSavedSettings={(settingsStr) => {
                setProfile((p) => ({ ...p, background_settings: settingsStr }));
                setBgPreview(null);
                setBgModal(false);
              }}
              onRemoved={() => {
                setProfile((p) => ({ ...p, backgroundPhoto: null, background_settings: null }));
                setBgPreview(null);
                setBgModal(false);
              }}
            />
          )}

          {/* ── ТАБЫ (только на своей странице) ────────────────── */}
          {isMe && (
            <div className="rb-up__tabs">
              <button
                type="button"
                className={`rb-up__tab ${activeView === "profile" ? "is-active" : ""}`}
                onClick={() => switchView("profile")}
              >
                <CabIcon name="user" size={15} />
                Профиль
              </button>
              <button
                type="button"
                className={`rb-up__tab ${activeView === "settings" ? "is-active" : ""}`}
                onClick={() => switchView("settings")}
              >
                <CabIcon name="gear" size={15} />
                Настройки
              </button>
              <button
                type="button"
                className={`rb-up__tab ${activeView === "security" ? "is-active" : ""}`}
                onClick={() => switchView("security")}
              >
                <CabIcon name="shield" size={15} />
                Безопасность
              </button>
            </div>
          )}

          {/* ── ВКЛАДКА «НАСТРОЙКИ» (только для себя) ──────────── */}
          {isMe && activeView === "settings" && (
            <div className="rb-up__settings-wrap">
              <ProfileScreen />
            </div>
          )}

          {/* ── ВКЛАДКА «БЕЗОПАСНОСТЬ» (только для себя) ───────── */}
          {isMe && activeView === "security" && (
            <div className="rb-up__settings-wrap">
              <AccountSub embedded={true} />
            </div>
          )}

          {/* ── МОДАЛКА «ПОДАРИТЬ» (на чужой странице) ─────────── */}
          {!isMe && giftModal && (
            <GiftModal
              toUserId={Number(userId)}
              toName={profile.firstName || "Пользователь"}
              onClose={() => setGiftModal(false)}
              onSent={(gift, diamondsLeft) => {
                setGiftShelf((s) => ({
                  gifts: [gift, ...((s && s.gifts) || [])],
                  count: ((s && s.count) || 0) + 1,
                }));
                setGiftModal(false);
              }}
            />
          )}

          {/* ── МОДАЛКА «ОФОРМЛЕНИЕ» (тема, секции, панели, шрифт,
              заголовки, виджеты) с живым предпросмотром на странице ── */}
          {isMe && layoutModal && (
            <LayoutModal
              layout={parseLayout(profile.profile_layout)}
              onPreview={setLayoutPreview}
              onClose={() => {
                setLayoutPreview(null);
                setLayoutModal(false);
              }}
              onSaved={(layoutStr) => {
                setProfile((p) => ({ ...p, profile_layout: layoutStr }));
                setLayoutPreview(null);
                setLayoutModal(false);
              }}
            />
          )}

          {/* ── МОДАЛКА «БЕЙДЖИ У ИМЕНИ» (только для себя) ─────── */}
          {isMe && badgesModal && (
            <BadgesModal
              onClose={() => setBadgesModal(false)}
              onSaved={(csv) => {
                setProfile((p) => ({ ...p, displayed_badges: csv || null }));
                setBadgesModal(false);
              }}
            />
          )}

          {/* ── МОДАЛКА ПРОГРЕССА ДОСТИЖЕНИЯ ───────────────────── */}
          {achievModal && (
            <AchievModal ach={achievModal} onClose={() => setAchievModal(null)} />
          )}

          {/* ── МОДАЛКА МЕДАЛИ ─────────────────────────────────── */}
          {medalModal && (
            <MedalModal medal={medalModal} onClose={() => setMedalModal(null)} />
          )}

          {/* ── МОДАЛКА «О СЕБЕ» (редактор оформления, только для себя) ── */}
          {isMe && aboutModal && (
            <AboutEditorModal
              currentPlain={profile.about || ""}
              currentRich={profile.about_rich || null}
              onClose={() => setAboutModal(false)}
              onSaved={(about, aboutRich) => {
                setProfile((p) => ({
                  ...p,
                  about: about || null,
                  about_rich: aboutRich || null,
                }));
                setAboutModal(false);
              }}
            />
          )}

          {/* ── «О СЕБЕ» — всегда первой, вне настраиваемого порядка ── */}
          {activeView === "profile" && (profile.about || isMe) && (
            <Section key="about" icon="user" title={layout.titles.about || "О себе"}
                     sub={profile.about ? null
                          : "Расскажи о себе — это увидят гости профиля"}>
              {isMe && (
                <button
                  type="button"
                  className="rb-up__about-edit"
                  onClick={() => setAboutModal(true)}
                >
                  <CabIcon name="pencil" size={13} />
                  {profile.about ? "Изменить" : "Написать"}
                </button>
              )}
              {profile.about && (
                <RichAbout rich={profile.about_rich} plain={profile.about} />
              )}
            </Section>
          )}

          {/* ── ВКЛАДКА «ПРОФИЛЬ» — секции в порядке из layout ─── */}
          {activeView === "profile" && layout.order.map((key) => {
            if (layout.hidden.includes(key)) return null;
            switch (key) {

            case "gifts": return (
          <Section key="gifts" icon="gift" title={sectionTitle("gifts")}
                   sub={giftShelf === null ? "Загружаем…"
                        : giftShelf.count === 0 ? (isMe ? "Тебе пока не дарили подарков" : "Подари первый подарок!")
                        : `${giftShelf.count} ${pluralGifts(giftShelf.count)}`}>
            {!isMe && (
              <button
                type="button"
                className="rb-up__gift-send-btn"
                onClick={() => setGiftModal(true)}
              >
                <CabIcon name="gift" size={15} />
                Подарить
              </button>
            )}
            {giftShelf && giftShelf.gifts.length > 0 && (
              <Collapsible
                maxHeight={260}
                label={`Показать все ${giftShelf.count} ${pluralGifts(giftShelf.count)}`}
              >
                <div className="rb-up__gifts">
                  {giftShelf.gifts.map((g) => (
                    <div key={g.id} className="rb-up__gift" title={
                      (g.anonymous ? "Аноним" : `От: ${g.from_name}`) +
                      (g.message ? ` — «${g.message}»` : "")
                    }>
                      <GiftIcon lottie={g.lottie} size={56} />
                      <span className="rb-up__gift-title">{g.title}</span>
                      <span className="rb-up__gift-from">
                        {g.anonymous ? "Аноним" : g.from_name}
                      </span>
                    </div>
                  ))}
                </div>
              </Collapsible>
            )}
          </Section>
            );

            case "games": return (
          <Section key="games" icon="gamepad" title={sectionTitle("games")}
                   sub={games === null ? "Загружаем…"
                        : games.length === 0 ? "Этот автор пока не опубликовал игр"
                        : `${games.length} ${pluralGames(games.length)}`}>
            {games === null ? (
              <div className="rb-feed__loading"><div className="rb-cab-spinner" /></div>
            ) : games.length > 0 ? (
              <Collapsible
                maxHeight={600}
                label={`Показать все ${games.length} ${pluralGames(games.length)}`}
              >
                <div className="rb-up__games-grid">
                  {games.map((g) => (
                    <GameCardMini key={g.id} game={g} onClick={(id) => navigate(`/app?game=${id}`)} />
                  ))}
                </div>
              </Collapsible>
            ) : null}
          </Section>
            );

            case "history": return (history && history.length > 0) ? (
            <Section key="history" icon="play" title={sectionTitle("history")}
                     sub="Последние 5 игр в которых был автор">
              <div className="rb-up__games-grid">
                {history.map((g) => (
                  <GameCardMini key={g.id} game={g} onClick={(id) => navigate(`/app?game=${id}`)} />
                ))}
              </div>
            </Section>
            ) : null;

            case "medals": return (
          <Section key="medals" icon="trophy" title={sectionTitle("medals")}
                   sub={medals === null ? "Загружаем…"
                        : medals.receivedCount === 0
                          ? "Медали получают за хиты, топ лидербордов и запуски игр"
                          : `Получено ${medals.receivedCount} из ${medals.total}`}>
            {medals && medals.medals.length > 0 && (
              <Collapsible
                maxHeight={420}
                label={`Показать все ${medals.total} ${pluralMedals(medals.total)}`}
              >
                <div className="rb-up__achievs rb-up__medals-grid">
                  {medals.medals.map((m) => (
                    <MedalCard key={m.key} medal={m} onClick={() => setMedalModal(m)} />
                  ))}
                </div>
              </Collapsible>
            )}
          </Section>
            );

            case "achievs": return (
          <Section key="achievs" icon="medal" title={sectionTitle("achievs")}
                   sub={achievs === null ? "Загружаем…"
                        : `Получено ${achievs.receivedCount} из ${achievs.total}`}>
            {achievs && achievs.achievs.length > 0 && (
              <Collapsible
                maxHeight={420}
                label={`Показать все ${achievs.total} ${pluralAch(achievs.total)}`}
              >
                <div className="rb-up__achievs">
                  {achievs.achievs.map((a) => (
                    <AchievCard key={a.key} ach={a} onClick={() => setAchievModal(a)} />
                  ))}
                </div>
              </Collapsible>
            )}
          </Section>
            );

            case "comments": return (
          <Section key="comments" icon="chat" title={sectionTitle("comments")}
                   sub={!comments ? "Загружаем…"
                        : comments.length === 0 ? "Пока никто ничего не написал"
                        : `${comments.length} ${pluralComments(comments.length)}`}>
            {!isMe && (
              <CommentForm
                value={commentText}
                onChange={setCommentText}
                onSubmit={submitComment}
                busy={commentBusy}
              />
            )}
            {comments && comments.length > 0 && (
              <div className="rb-up__comments">
                {comments.map((c) => {
                  // Кнопка-корзинка видна когда юзер реально может удалить:
                  //   1. Это моя страница (любой коммент на ней) — isMe=true
                  //   2. Это мой коммент (на чужой странице)
                  // Сравниваем с myIdFromJwt — единственный источник правды,
                  // который не зависит от race-условий с AuthContext.
                  const authorId = c.userId ?? c.user_id ?? null;
                  const isMyComment = sameUser(authorId, myIdFromJwt);
                  return (
                    <CommentRow
                      key={c.id}
                      comment={c}
                      canDelete={isMe || isMyComment}
                      onDelete={() => removeComment(c.id)}
                      navigate={navigate}
                    />
                  );
                })}
              </div>
            )}
          </Section>
            );

            default: return null;
            }
          })}
        </div>
      )}
    </CabinetLayout>
  );
}

export default function UserProfilePage() {
  return (
    <CabinetProvider>
      <UserProfileInner />
    </CabinetProvider>
  );
}

/* ═════════════════════════════════════════════════════════════════════
 * Оформление профиля: темы шапки + порядок/скрытие секций.
 * Хранится в Profile.profile_layout (JSON), видно всем посетителям.
 * ═════════════════════════════════════════════════════════════════════ */

// Ключи тем согласованы с белым списком бэка (Cosmetics.py LAYOUT_THEMES).
const HEADER_THEMES = {
  default: { title: "Классика", bg: "linear-gradient(135deg, #1a237e 0%, #3357ff 50%, #6d28d9 100%)" },
  ocean:   { title: "Океан",    bg: "linear-gradient(135deg, #0f3460 0%, #0e7490 55%, #22d3ee 100%)" },
  sunset:  { title: "Закат",    bg: "linear-gradient(135deg, #7c2d12 0%, #ea580c 55%, #f472b6 100%)" },
  forest:  { title: "Лес",      bg: "linear-gradient(135deg, #14532d 0%, #16a34a 60%, #a3e635 100%)" },
  space:   { title: "Космос",   bg: "linear-gradient(135deg, #0f0f23 0%, #4c1d95 55%, #c026d3 100%)" },
  candy:   { title: "Конфета",  bg: "linear-gradient(135deg, #be185d 0%, #ec4899 55%, #a78bfa 100%)" },
  fire:    { title: "Огонь",    bg: "linear-gradient(135deg, #7f1d1d 0%, #dc2626 55%, #f59e0b 100%)" },
  night:   { title: "Ночь",     bg: "linear-gradient(135deg, #0b1020 0%, #1e293b 60%, #475569 100%)" },
};

const SECTION_TITLES = {
  gifts: "Подарки",
  games: "Игры автора",
  history: "Недавно играл",
  medals: "Медали Рублокса",
  achievs: "Достижения Рублокса",
  comments: "Гостевая книга",
};
// «medals» вернулся 2026-07-09 — теперь это СВОИ медали Рублокса
// (лидерборды/хиты недели/запуски), а не минкрафтийские.
const DEFAULT_ORDER = ["gifts", "games", "history", "medals", "achievs", "comments"];

// ── Стиль панелей-секций (Б4). Ключи согласованы с бэком (PANEL_BORDERS).
const PANEL_BORDER_TITLES = {
  none:   "Обычная",
  soft:   "Мягкая",
  accent: "Синяя",
  neon:   "Неон",
  gold:   "Золото",
  candy:  "Конфета",
};
const PANEL_DEFAULTS = { border: "none", radius: null, glass: null, tint: "" };

// ── Шрифт заголовков секций (Б5): те же ключи, что у «О себе».
const TITLE_FONT_TITLES = {
  default: "Обычный",
  serif:   "С засечками",
  mono:    "Моноширинный",
  rounded: "Округлый",
  hand:    "Рукописный",
};
const TITLE_FONT_STACKS = {
  default: "",
  serif: "Georgia, 'Times New Roman', serif",
  mono: "Consolas, 'Courier New', monospace",
  rounded: "'Comic Sans MS', 'Comic Sans', cursive",
  hand: "'Segoe Script', 'Bradley Hand', cursive",
};

// ── Стат-виджеты шапки (В1). Ключи согласованы с бэком (WIDGET_KEYS),
// значения отдаёт GET /rublox-stats/<id>.
const WIDGET_DEFS = {
  plays:   { icon: "play",     label: "запусков игр",    color: "#0ea5e9" },
  games:   { icon: "gamepad",  label: "игр создано",     color: "#6d28d9" },
  likes:   { icon: "heart",    label: "лайков",          color: "#e8445a" },
  friends: { icon: "users",    label: "друзей",          color: "#16a34a" },
  gifts:   { icon: "gift",     label: "подарков",        color: "#ec4899" },
  days:    { icon: "calendar", label: "дней на Рублоксе", color: "#f59e0b" },
  achievs: { icon: "medal",    label: "достижений",      color: "#3357ff" },
  medals:  { icon: "trophy",   label: "медалей",         color: "#d97706" },
};
const WIDGETS_MAX = 4;

// ── Бейджи у имени (А4). Ключи согласованы с бэком (Cosmetics.py BADGES).
const BADGE_DEFS = {
  pioneer:    { title: "Первопроходец",   icon: "rocket" },
  veteran:    { title: "Ветеран",         icon: "shield" },
  author:     { title: "Автор игр",       icon: "gamepad" },
  hitmaker:   { title: "Хитмейкер",       icon: "fire" },
  champion:   { title: "Чемпион",         icon: "trophy" },
  top_author: { title: "Топ-автор",       icon: "crown" },
  team:       { title: "Команда Рублокса", icon: "check" },
};

/** CSS-переменные стиля панелей на контейнер .rb-up (только заданные). */
function panelStyleVars(panel) {
  const style = {};
  if (panel.tint) style["--rb-panel-tint"] = panel.tint;
  if (panel.radius != null) style["--rb-panel-radius"] = `${panel.radius}px`;
  /* glass 0..90 — «прозрачность» панели; в CSS уходит НЕпрозрачность.
     Переменную ставим ВСЕГДА, даже при glass === null. Иначе получалось
     расхождение: ползунок показывает «0%», человек ждёт сплошную панель,
     а переменная не задана — и в CSS срабатывает фолбэк (92% поверх фона),
     то есть панель на 8% просвечивает. Выбранный белый выглядел серым
     именно поэтому. */
  style["--rb-panel-alpha"] = `${100 - (panel.glass ?? 0)}%`;
  return style;
}

/** Разбор profile_layout (JSON-строка или null) в
 * {theme, order, hidden, panel, titleFont, titles, widgets}. */
function parseLayout(profileLayout) {
  const def = {
    theme: "default", order: DEFAULT_ORDER, hidden: [],
    panel: PANEL_DEFAULTS, titleFont: "default", titles: {}, widgets: [],
  };
  if (!profileLayout) return def;
  try {
    const obj = JSON.parse(profileLayout);
    const theme = obj?.theme && HEADER_THEMES[obj.theme] ? obj.theme : "default";
    let order = Array.isArray(obj?.order)
      ? obj.order.filter((k) => DEFAULT_ORDER.includes(k))
      : [];
    // недостающие секции — в конец (защита от старых сохранений)
    order = order.length
      ? [...order, ...DEFAULT_ORDER.filter((k) => !order.includes(k))]
      : DEFAULT_ORDER;
    const hidden = Array.isArray(obj?.hidden)
      ? obj.hidden.filter((k) => DEFAULT_ORDER.includes(k))
      : [];

    const clampInt = (v, lo, hi) => {
      const n = Number(v);
      return Number.isFinite(n) ? Math.min(hi, Math.max(lo, Math.round(n))) : null;
    };
    const p = (obj?.panel && typeof obj.panel === "object") ? obj.panel : {};
    const panel = {
      border: PANEL_BORDER_TITLES[p.border] ? p.border : "none",
      radius: p.radius != null ? clampInt(p.radius, 0, 28) : null,
      glass: p.glass != null ? clampInt(p.glass, 0, 90) : null,
      tint: (typeof p.tint === "string" && /^#[0-9a-f]{6}$/i.test(p.tint)) ? p.tint : "",
    };

    const titleFont = TITLE_FONT_TITLES[obj?.titleFont] ? obj.titleFont : "default";

    const titles = {};
    if (obj?.titles && typeof obj.titles === "object") {
      for (const k of [...Object.keys(SECTION_TITLES), "about"]) {
        const t = obj.titles[k];
        if (typeof t === "string" && t.trim()) titles[k] = t.trim().slice(0, 30);
      }
    }

    const widgets = Array.isArray(obj?.widgets)
      ? obj.widgets.filter((k) => WIDGET_DEFS[k]).slice(0, WIDGETS_MAX)
      : [];

    return { theme, order, hidden, panel, titleFont, titles, widgets };
  } catch {
    return def;
  }
}

/* ═════════════════════════════════════════════════════════════════════
 * Настройки отображения картинок профиля (фон страницы и обложка шапки).
 * JSON в Profile.background_settings / cover_settings, диапазоны
 * согласованы с whitelist бэка (_IMG_SETTINGS_RANGES).
 * ═════════════════════════════════════════════════════════════════════ */

// Дефолтные затемнения подобраны под прежние фиксированные вуали.
// mode: cover — заполнить; tile — плитка; height — во всю высоту БЕЗ
// повтора (вертикальные картинки); width — во всю ширину без повтора.
// fill — чем заполнять края в height/width: blur (размытая копия) / dark.
// blur — размытие самой картинки (px).
const BG_DEFAULTS = { mode: "cover", posX: 50, posY: 50, zoom: 100, tile: 320, dim: 52, blur: 0, fill: "blur" };
const COVER_DEFAULTS = { mode: "cover", posX: 50, posY: 50, zoom: 100, tile: 320, dim: 42, blur: 0, fill: "blur" };
const IMG_MODES = ["cover", "tile", "height", "width"];

/** Разбор JSON-строки настроек (или null) с clamp в диапазоны. */
function parseImgSettings(str, defaults) {
  if (!str) return defaults;
  const clamp = (v, lo, hi, def) => {
    const n = Number(v);
    return Number.isFinite(n) ? Math.min(hi, Math.max(lo, Math.round(n))) : def;
  };
  try {
    const o = JSON.parse(str);
    return {
      mode: IMG_MODES.includes(o?.mode) ? o.mode : "cover",
      posX: clamp(o?.posX, 0, 100, defaults.posX),
      posY: clamp(o?.posY, 0, 100, defaults.posY),
      zoom: clamp(o?.zoom, 100, 300, defaults.zoom),
      tile: clamp(o?.tile, 64, 1024, defaults.tile),
      dim: clamp(o?.dim, 0, 85, defaults.dim),
      blur: clamp(o?.blur, 0, 30, defaults.blur),
      fill: o?.fill === "dark" ? "dark" : "blur",
    };
  } catch {
    return defaults;
  }
}

/** Inline-стиль слоя картинки (.rb-up__bg или .rb-up__cover) из настроек.
 * Сила вуали уходит в CSS-вар --rb-dim (::after нельзя стилизовать инлайном). */
function imgLayerStyle(url, s) {
  const style = {
    backgroundImage: `url(${url})`,
    "--rb-dim": s.dim / 100,
  };
  let scale = 1;
  if (s.mode === "tile") {
    // Маленькая картинка тиражируется по X и Y; tile — ширина одной плитки
    style.backgroundRepeat = "repeat";
    style.backgroundSize = `${s.tile}px auto`;
    style.backgroundPosition = `${s.posX}% ${s.posY}%`;
  } else if (s.mode === "height") {
    // Вертикальная картинка во всю высоту, без повтора; двигается по X
    style.backgroundRepeat = "no-repeat";
    style.backgroundSize = "auto 100%";
    style.backgroundPosition = `${s.posX}% center`;
  } else if (s.mode === "width") {
    // Во всю ширину, без повтора; двигается по Y
    style.backgroundRepeat = "no-repeat";
    style.backgroundSize = "100% auto";
    style.backgroundPosition = `center ${s.posY}%`;
  } else {
    style.backgroundRepeat = "no-repeat";
    style.backgroundSize = "cover";
    style.backgroundPosition = `${s.posX}% ${s.posY}%`;
    // Приближение поверх cover — масштабируем сам слой (края уходят
    // за вьюпорт, позиция по X/Y выбирает видимую область)
    if (s.zoom > 100) scale = s.zoom / 100;
  }
  if (s.blur > 0) {
    style.filter = `blur(${s.blur}px)`;
    // края размытого слоя «светятся» — слегка растягиваем слой за вьюпорт
    scale = Math.max(scale, 1 + s.blur * 0.01);
  }
  if (scale > 1) style.transform = `scale(${scale.toFixed(3)})`;
  return style;
}

/** Слой-подложка для режимов height/width (fill="blur"): та же картинка,
 * растянутая cover и сильно размытая — красиво заполняет пустые края. */
function imgFillStyle(url) {
  return {
    backgroundImage: `url(${url})`,
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    backgroundPosition: "center",
    filter: "blur(26px) brightness(0.85)",
    transform: "scale(1.15)",
  };
}

/* ═════════════════════════════════════════════════════════════════════
 * Под-компоненты (продолжение)
 * ═════════════════════════════════════════════════════════════════════ */

/* ═════════════════════════════════════════════════════════════════════
 * Под-компоненты
 * ═════════════════════════════════════════════════════════════════════ */

/* Стиль для цветного имени: hex — просто цвет, 'rainbow' — градиент по тексту. */
function nameColorStyle(color) {
  if (!color) return undefined;
  if (color === "rainbow") {
    return {
      backgroundImage: "linear-gradient(90deg,#ff5db1,#ff8a00,#ffe600,#00d26a,#37c6ff,#a855f7)",
      WebkitBackgroundClip: "text",
      backgroundClip: "text",
      color: "transparent",
      textShadow: "none",
    };
  }
  return { color, textShadow: "0 1px 6px rgba(0,0,0,0.35)" };
}

function ProfileHeader({ profile, extras, isMe, judgement, onVote, navigate, onOpenSettings, onEditCover, onEditBackground, onOpenShop, onOpenLayout, onOpenBadges, widgetKeys, widgetStats, headerTheme, myStars, onOpenWallet, cover }) {
  const photoUrl = profile.photo ? profilePhotoSrc(profile.photo) : null;
  const frameKey = profile.active_frame || "none";
  // Тема шапки видна когда нет обложки (обложка перекрывает градиент).
  const themeBg = (HEADER_THEMES[headerTheme] || HEADER_THEMES.default).bg;
  const username = profile.firstName || "Пользователь";
  const initial = String(username).charAt(0).toUpperCase();
  const level = profile.level ?? 0;
  const diamonds = extras?.diamond ?? null;
  const rating = extras?.rating ?? null;
  const confirmed = !!extras?.confirmed;
  const hasSubscription = !!extras?.has_subscription;
  // judgement бэка — одно число: сумма +/-. Майнкрафтия использует ту же модель.
  // Одно нажатие в одну сторону на одного юзера, потом «Уже изменено».
  const score = Number(judgement?.judgement ?? 0);
  // Заработанные бейджи, которые владелец выбрал показывать у имени (до 3).
  const badgeKeys = (profile.displayed_badges || "")
    .split(",").map((s) => s.trim()).filter((k) => BADGE_DEFS[k]);

  return (
    <div className="rb-up__header" style={{ background: themeBg }}>
      {/* Кастомная обложка (20 алмазов). Абсолютный слой поверх градиента,
          отображение (режим/позиция/масштаб/затемнение/размытие) — из
          cover_settings (или превью модалки — в т.ч. ДО покупки), затемнение
          слева — чтобы имя и статы оставались читаемыми. В height/width
          края заполняет размытая копия картинки (слой --fill). */}
      {cover && (
        <>
          {(cover.s.mode === "height" || cover.s.mode === "width")
            && cover.s.fill !== "dark" && (
            <div className="rb-up__cover rb-up__cover--fill" style={imgFillStyle(cover.url)} />
          )}
          <div className="rb-up__cover" style={imgLayerStyle(cover.url, cover.s)} />
        </>
      )}
      <div className="rb-up__header-glow" />
      {frameKey !== "none" ? (
        <AvatarFrame
          frame={frameKey}
          size={118}
          thickness={5}
          style={{ position: "relative", zIndex: 1 }}
        >
          <div className="rb-up__avatar" style={{ width: "100%", height: "100%", border: "none" }}>
            {photoUrl ? (
              <img src={photoUrl} alt={username} onError={(e) => { e.currentTarget.style.display = "none"; }} />
            ) : (
              <span style={{
                background: avatarInitialColor(username),
                color: "#fff",
              }}>{initial}</span>
            )}
          </div>
        </AvatarFrame>
      ) : (
        <div className="rb-up__avatar">
          {photoUrl ? (
            <img src={photoUrl} alt={username} onError={(e) => { e.currentTarget.style.display = "none"; }} />
          ) : (
            <span style={{
              background: avatarInitialColor(username),
              color: "#fff",
            }}>{initial}</span>
          )}
        </div>
      )}
      <div className="rb-up__info">
        <h1 className="rb-up__name" style={nameColorStyle(profile.name_color)}>
          {username}
          {profile.lastName ? <span className="rb-up__lastname"> {profile.lastName}</span> : null}
        </h1>
        <div className="rb-up__sub">
          {profile.country ? <span><CabIcon name="globe" size={13} /> {profile.country}</span> : null}
          {profile.city ? <span>{profile.city}</span> : null}
          {profile.status ? <span className="rb-up__status">«{profile.status}»</span> : null}
        </div>
        {/* Бейджи статуса — email подтверждён / подписка активна.
            Видны на странице любого профиля (публичные «знаки доверия»).
            Первыми — заработанные бейджи, выбранные владельцем (до 3). */}
        <div className="rb-up__badges">
          {badgeKeys.map((k) => (
            <span
              key={k}
              className={`rb-up__badge rb-up__badge--earned rb-up__badge--bk-${k}`}
              title={BADGE_DEFS[k].title}
            >
              <CabIcon name={BADGE_DEFS[k].icon} size={12} />
              {BADGE_DEFS[k].title}
            </span>
          ))}
          {isMe && (
            <button
              type="button"
              className="rb-up__badge rb-up__badge--edit"
              onClick={onOpenBadges}
              title="Выбрать бейджи у имени (заработанные)"
            >
              <CabIcon name="pencil" size={11} />
              Бейджи
            </button>
          )}
          {confirmed && (
            <span className="rb-up__badge rb-up__badge--confirmed" title="Email подтверждён">
              <CabIcon name="check" size={12} />
              Подтверждённый
            </span>
          )}
          {hasSubscription && (
            <span className="rb-up__badge rb-up__badge--sub" title="Активная подписка Майнкрафтии">
              <CabIcon name="diamond" size={12} />
              Подписка
            </span>
          )}
          {/* Аккаунт экосистемы единый (user_id общий с Маськой) — просто
              ссылка на профиль в Маське. Откроется у залогиненного. */}
          <a
            className="rb-up__badge rb-up__badge--maska"
            href={`https://маська.рф/?u=${profile.id}`}
            target="_blank"
            rel="noopener noreferrer"
            title="Открыть этот профиль в мессенджере Маська"
          >
            <CabIcon name="chat" size={12} />
            Профиль в Маське
          </a>
        </div>
        <div className="rb-up__stats">
          <StatChip icon="crown" tone="violet" value={level} label="уровень" />
          {diamonds != null && (
            <StatChip icon="diamond" tone="cyan" value={formatCompact(diamonds)} label="алмазов"
                      onClick={isMe ? onOpenWallet : undefined}
                      title={isMe ? "Открыть кошелёк" : undefined} />
          )}
          {/* звёзды приватные — показываем только владельцу; клик = кошелёк */}
          {isMe && myStars != null && (
            <StatChip icon="star" tone="gold" value={formatCompact(myStars)} label="звёзд"
                      onClick={onOpenWallet} title="Открыть кошелёк" />
          )}
          {rating != null && <StatChip icon="trophy" tone="yellow" value={formatCompact(rating)} label="рейтинг" />}
        </div>
        {/* Стат-виджеты (В1): счётчики, которые владелец выбрал в
            «Оформлении». Значения — GET /rublox-stats, видны всем. */}
        {widgetKeys && widgetKeys.length > 0 && (
          <div className="rb-up__widgets">
            {widgetKeys.map((k) => {
              const def = WIDGET_DEFS[k];
              if (!def) return null;
              const v = widgetStats ? Number(widgetStats[k] ?? 0) : null;
              return (
                <div key={k} className="rb-up__widget" style={{ "--w-color": def.color }}>
                  <span className="rb-up__widget-ico"><CabIcon name={def.icon} size={16} /></span>
                  <strong className="rb-up__widget-value">
                    {v == null ? "…" : formatCompact(v)}
                  </strong>
                  <span className="rb-up__widget-label">{def.label}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className="rb-up__header-actions">
        {isMe ? (
          /* Было пять одинаковых кнопок столбиком — визуально равнозначных,
             хотя четыре из них про одно и то же (оформление). Главное
             действие не выделялось, и колонка занимала всю высоту шапки.
             Теперь: «Редактировать» — единственная главная кнопка, ниже
             компактный ряд иконок-кнопок кастомизации с подписями.
             Правило дизайн-системы: primary на экране ровно одна. */
          <>
            <button
              type="button"
              className="rb-up__edit-btn rb-up__edit-btn--primary"
              onClick={onOpenSettings}
            >
              <CabIcon name="pencil" size={15} />
              Редактировать
            </button>
            <div className="rb-up__custom-row">
              <button
                type="button"
                className="rb-up__custom-btn"
                onClick={onEditCover}
                title="Изменить обложку профиля"
              >
                <CabIcon name="image" size={16} />
                <span>Обложка</span>
              </button>
              <button
                type="button"
                className="rb-up__custom-btn"
                onClick={onEditBackground}
                title="Фон всей страницы профиля"
              >
                <CabIcon name="wallpaper" size={16} />
                <span>Фон</span>
              </button>
              <button
                type="button"
                className="rb-up__custom-btn"
                onClick={onOpenShop}
                title="Рамки аватара и цвет имени"
              >
                <CabIcon name="palette" size={16} />
                <span>Магазин</span>
              </button>
              <button
                type="button"
                className="rb-up__custom-btn"
                onClick={onOpenLayout}
                title="Тема шапки, порядок и скрытие секций"
              >
                <CabIcon name="sparkles" size={16} />
                <span>Вид</span>
              </button>
            </div>
          </>
        ) : (
          <div className="rb-up__judgement">
            <button
              type="button"
              className="rb-up__vote rb-up__vote--plus"
              onClick={() => onVote("+")}
              title="Плюс к репутации"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2.5"
                   strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
            <span className={`rb-up__score ${score > 0 ? "is-positive" : score < 0 ? "is-negative" : ""}`}>
              {score > 0 ? `+${score}` : score}
            </span>
            <button
              type="button"
              className="rb-up__vote rb-up__vote--minus"
              onClick={() => onVote("-")}
              title="Минус к репутации"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2.5"
                   strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Цены зашиты на бэке; здесь — только для подписи кнопок.
const COVER_PRICE = 20;
const BACKGROUND_PRICE = 80;

/* Модалка «О себе» переехала в components/cabinet/AboutEditor.jsx —
 * теперь это полноценный редактор оформления (жирный/цвет/размер/шрифт). */

/* ── Модалка платной картинки с «крутилками» (обложка / фон) ────
 * Загрузка картинки (цена в price) + бесплатные настройки отображения:
 * режим заполнить/плитка (тиражирование по X и Y для маленьких картинок),
 * позиция X/Y, масштаб, размер плитки, сила затемнения. Пока модалка
 * открыта — живой предпросмотр на странице (onPreview), оверлей почти
 * прозрачный, чтобы страницу позади было видно. */

/** Строка-«крутилка»: подпись + текущее значение + range-слайдер. */
function BgSlider({ label, value, unit, min, max, step, onChange }) {
  return (
    <label className="rb-up__bgset-row">
      <span className="rb-up__bgset-label">
        {label} <b>{value}{unit}</b>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step || 1}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </label>
  );
}

function ImageSettingsModal({ title, icon, price, defaults,
                              hintNoImage, hintHasImage, removeLabel,
                              hasImage, current, diamonds,
                              doUpload, doRemove, doSaveSettings,
                              resultKey, settingsKey,
                              onPreview, onClose,
                              onUploaded, onSavedSettings, onRemoved }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [s, setS] = useState(current);   // черновик настроек
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!file) { setPreview(null); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Живой предпросмотр на странице позади модалки. url — локальный файл
  // ДО покупки (посмотреть и подогнать настройки перед тратой алмазов);
  // без выбранного файла превьюится уже установленная картинка.
  useEffect(() => {
    if (preview || hasImage) onPreview({ url: preview, s });
    // s передаётся из set() тем же объектом — эффект нужен на смену файла
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview]);

  const set = (key, value) => {
    setS((prev) => {
      const next = { ...prev, [key]: value };
      onPreview({ url: preview, s: next });
      return next;
    });
    setDirty(true);
  };

  const pickFile = (e) => {
    setError("");
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (!/^image\/(jpeg|png|gif)$/i.test(f.type)) {
      setError("Подойдёт JPG, PNG или GIF (гифки будут двигаться)");
      return;
    }
    // GIF тяжелее (анимация) — лимит выше, согласован с бэком (15 МБ)
    const maxMb = /gif$/i.test(f.type) ? 15 : 8;
    if (f.size > maxMb * 1024 * 1024) {
      setError(`Файл слишком большой (максимум ${maxMb} МБ)`);
      return;
    }
    setFile(f);
  };

  const submitUpload = async () => {
    if (!file || busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await doUpload(file);
      setFile(null);
      onUploaded(res[resultKey], res.diamonds);
      if (dirty) {
        // Настройки подогнали ещё до покупки — сразу сохраняем их,
        // чтобы гости увидели картинку ровно как в предпросмотре.
        const res2 = await doSaveSettings(s);
        onSavedSettings(res2[settingsKey]);
        return; // onSavedSettings закрывает модалку
      }
      // Модалка остаётся открытой — можно крутить настройки отображения
      setBusy(false);
    } catch (e) {
      setError(e?.message || "Не удалось загрузить картинку");
      setBusy(false);
    }
  };

  const saveSettings = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await doSaveSettings(s);
      onSavedSettings(res[settingsKey]);
    } catch (e) {
      setError(e?.message || "Не удалось сохранить настройки");
      setBusy(false);
    }
  };

  const resetSettings = () => {
    setS(defaults);
    onPreview({ url: preview, s: defaults });
    setDirty(true);
  };

  const remove = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await doRemove();
      onRemoved();
    } catch (e) {
      setError(e?.message || "Не удалось убрать картинку");
      setBusy(false);
    }
  };

  const enough = diamonds == null || diamonds >= price;

  return (
    <div className="rb-up__modal-overlay rb-up__modal-overlay--lite" onClick={onClose}>
      <div className="rb-up__modal" onClick={(e) => e.stopPropagation()}>
        <div className="rb-up__modal-head">
          <div className="rb-up__modal-title">
            <CabIcon name={icon} size={18} />
            {title}
          </div>
          <button type="button" className="rb-up__modal-close" onClick={onClose} aria-label="Закрыть">
            <CabIcon name="close" size={16} />
          </button>
        </div>

        <div className="rb-up__modal-body">
          <p className="rb-up__modal-hint">
            {hasImage ? hintHasImage : hintNoImage}
          </p>

          <label className={`rb-up__cover-drop ${preview ? "has-preview" : ""}`}>
            {preview ? (
              <img src={preview} alt="Предпросмотр" />
            ) : (
              <span className="rb-up__cover-drop-text">
                <CabIcon name="image" size={26} />
                {hasImage ? "Сменить картинку…" : "Выбрать картинку…"}
              </span>
            )}
            <input
              type="file"
              accept="image/jpeg,image/png,image/gif"
              onChange={pickFile}
              hidden
            />
          </label>

          <div className="rb-up__cover-price">
            <span className="rb-up__cover-price-tag">
              <CabIcon name="diamond" size={14} />
              {hasImage ? "Смена картинки" : "Установка"} — {price} алмазов
            </span>
            {diamonds != null && (
              <span className={`rb-up__cover-balance ${enough ? "" : "is-short"}`}>
                у тебя {formatCompact(diamonds)}
              </span>
            )}
          </div>

          {/* ── Крутилки отображения (бесплатно). Доступны и ДО покупки —
              выбранный файл сразу показывается на странице, настройки
              подгоняются заранее и сохранятся вместе с покупкой. ── */}
          {(hasImage || preview) && (
            <div className="rb-up__bgset">
              <div className="rb-up__bgset-title">Как показывать картинку</div>
              {!hasImage && preview && (
                <p className="rb-up__modal-hint" style={{ marginBottom: 8 }}>
                  Это предпросмотр — картинка появится у всех после нажатия
                  «Установить». Настройки сохранятся вместе с покупкой.
                </p>
              )}

              <div className="rb-up__bgset-modes">
                <button
                  type="button"
                  className={s.mode === "cover" ? "is-active" : ""}
                  onClick={() => set("mode", "cover")}
                  title="Картинка растягивается на весь экран (края обрезаются)"
                >
                  Заполнить
                </button>
                <button
                  type="button"
                  className={s.mode === "tile" ? "is-active" : ""}
                  onClick={() => set("mode", "tile")}
                  title="Картинка тиражируется по X и Y — для маленьких картинок и паттернов"
                >
                  Плитка
                </button>
                <button
                  type="button"
                  className={s.mode === "height" ? "is-active" : ""}
                  onClick={() => set("mode", "height")}
                  title="Картинка целиком во всю высоту, без повтора — для вертикальных картинок"
                >
                  Вся высота
                </button>
                <button
                  type="button"
                  className={s.mode === "width" ? "is-active" : ""}
                  onClick={() => set("mode", "width")}
                  title="Картинка целиком во всю ширину, без повтора"
                >
                  Вся ширина
                </button>
              </div>

              {(s.mode === "height" || s.mode === "width") && (
                <div className="rb-up__bgset-modes rb-up__bgset-modes--fill">
                  <span className="rb-up__bgset-label">Края:</span>
                  <button
                    type="button"
                    className={s.fill !== "dark" ? "is-active" : ""}
                    onClick={() => set("fill", "blur")}
                    title="Пустые края заполняет размытая копия картинки"
                  >
                    Размытие
                  </button>
                  <button
                    type="button"
                    className={s.fill === "dark" ? "is-active" : ""}
                    onClick={() => set("fill", "dark")}
                    title="Пустые края остаются тёмными"
                  >
                    Тёмные
                  </button>
                </div>
              )}

              {s.mode === "cover" && (
                <BgSlider label="Масштаб" unit="%" min={100} max={300} step={5}
                          value={s.zoom} onChange={(v) => set("zoom", v)} />
              )}
              {s.mode === "tile" && (
                <BgSlider label="Размер плитки" unit="px" min={64} max={1024} step={16}
                          value={s.tile} onChange={(v) => set("tile", v)} />
              )}
              {s.mode !== "width" && (
                <BgSlider label="Позиция по X" unit="%" min={0} max={100}
                          value={s.posX} onChange={(v) => set("posX", v)} />
              )}
              {s.mode !== "height" && (
                <BgSlider label="Позиция по Y" unit="%" min={0} max={100}
                          value={s.posY} onChange={(v) => set("posY", v)} />
              )}
              <BgSlider label="Затемнение" unit="%" min={0} max={85}
                        value={s.dim} onChange={(v) => set("dim", v)} />
              <BgSlider label="Размытие картинки" unit="px" min={0} max={30}
                        value={s.blur} onChange={(v) => set("blur", v)} />

              {hasImage && (
                <div className="rb-up__modal-actions">
                  <button
                    type="button"
                    className="rb-up__modal-btn rb-up__modal-btn--primary"
                    disabled={busy || !dirty}
                    onClick={saveSettings}
                  >
                    {busy ? "Сохраняем…" : "Сохранить настройки"}
                  </button>
                  <button
                    type="button"
                    className="rb-up__modal-btn rb-up__modal-btn--ghost"
                    disabled={busy}
                    onClick={resetSettings}
                  >
                    Сбросить
                  </button>
                </div>
              )}
            </div>
          )}

          {error && <div className="rb-up__modal-error">{error}</div>}

          <div className="rb-up__modal-actions">
            <button
              type="button"
              className="rb-up__modal-btn rb-up__modal-btn--primary"
              disabled={!file || busy || !enough}
              onClick={submitUpload}
            >
              {busy ? "Загрузка…" : !enough ? "Не хватает алмазов" : `Установить за ${price}`}
            </button>
            {hasImage && (
              <button
                type="button"
                className="rb-up__modal-btn rb-up__modal-btn--ghost"
                disabled={busy}
                onClick={remove}
              >
                {removeLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


/* ── Модалка «Подарить» ─────────────────────────────────────────
 * Каталог из 18 анимированных подарков (Lottie), подпись, галочка
 * анонимности. Цены в алмазах, списываются при отправке. */
function GiftModal({ toUserId, toName, onClose, onSent }) {
  const [catalog, setCatalog] = useState(null);
  const [selected, setSelected] = useState(null); // key
  const [message, setMessage] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [diamonds, setDiamonds] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const ctrl = new AbortController();
    getGiftsCatalog(ctrl.signal).then(setCatalog).catch(() => setCatalog([]));
    getMyDiamonds(ctrl.signal).then(setDiamonds).catch(() => setDiamonds(null));
    return () => ctrl.abort();
  }, []);

  const sel = (catalog || []).find((g) => g.key === selected) || null;
  const enough = sel ? (diamonds == null || diamonds >= sel.price) : true;

  const submit = async () => {
    if (!sel || busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await sendGift(toUserId, sel.key, message.trim(), anonymous);
      if (res && res.ok === false) {
        setError(res.message || "Не получилось отправить");
        return;
      }
      onSent(res.gift, res.diamonds);
    } catch (e) {
      setError(e?.message || "Не получилось отправить");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rb-up__modal-overlay" onClick={onClose}>
      <div className="rb-up__modal rb-up__modal--shop" onClick={(e) => e.stopPropagation()}>
        <div className="rb-up__modal-head">
          <div className="rb-up__modal-title">
            <CabIcon name="gift" size={18} />
            Подарок для {toName}
          </div>
          <button type="button" className="rb-up__modal-close" onClick={onClose} aria-label="Закрыть">
            <CabIcon name="close" size={16} />
          </button>
        </div>

        <div className="rb-up__modal-body">
          <div className="rb-up__shop-balances">
            <span className="rb-up__shop-balance">
              <CabIcon name="diamond" size={14} />
              {diamonds == null ? "…" : formatCompact(diamonds)} алмазов
            </span>
          </div>

          {error && <div className="rb-up__modal-error">{error}</div>}

          {catalog === null ? (
            <div className="rb-feed__loading"><div className="rb-cab-spinner" /></div>
          ) : (
            <div className="rb-up__shop-grid">
              {catalog.map((g) => (
                <button
                  key={g.key}
                  type="button"
                  className={`rb-up__shop-item ${selected === g.key ? "is-active" : ""}`}
                  onClick={() => setSelected(g.key)}
                  title={g.title}
                >
                  <GiftIcon lottie={g.lottie} size={56} />
                  <span className="rb-up__shop-item-title">{g.title}</span>
                  <span className="rb-up__shop-item-price">
                    <CabIcon name="diamond" size={11} />
                    {g.price}
                  </span>
                </button>
              ))}
            </div>
          )}

          <input
            type="text"
            className="rb-up__gift-message"
            placeholder="Подпись к подарку (необязательно)"
            maxLength={200}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          <label className="rb-up__gift-anon">
            <input
              type="checkbox"
              checked={anonymous}
              onChange={(e) => setAnonymous(e.target.checked)}
            />
            Подарить анонимно
          </label>

          <div className="rb-up__modal-actions">
            <button
              type="button"
              className="rb-up__modal-btn rb-up__modal-btn--primary"
              disabled={!sel || busy || !enough}
              onClick={submit}
            >
              {busy ? "Отправка…"
                : !sel ? "Выбери подарок"
                : !enough ? "Не хватает алмазов"
                : `Подарить за ${sel.price}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatChip({ icon, tone, value, label, onClick, title }) {
  // с onClick чип становится кнопкой (например «алмазы/звёзды» → кошелёк)
  if (onClick) {
    return (
      <button type="button" title={title} onClick={onClick}
              className={`rb-up__chip rb-up__chip--${tone} rb-up__chip--btn`}>
        <CabIcon name={icon} size={15} />
        <strong>{value}</strong>
        <span>{label}</span>
      </button>
    );
  }
  return (
    <div className={`rb-up__chip rb-up__chip--${tone}`}>
      <CabIcon name={icon} size={15} />
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

function Section({ icon, title, sub, children }) {
  return (
    <section className="rb-up__section">
      <div className="rb-cab-sechead">
        <span className="rb-cab-sechead__ico"><CabIcon name={icon} size={17} /></span>
        <div>
          <div className="rb-cab-sechead__title">{title}</div>
          <div className="rb-cab-sechead__sub">{sub}</div>
        </div>
      </div>
      {children}
    </section>
  );
}

/** Свёрнутая секция: сетка обрезается по высоте с мягким fade-градиентом
 * вместо горизонтального скролла. Кнопка «Показать все N…» появляется
 * только когда контент реально не влезает (ResizeObserver пересчитывает
 * при изменении ширины — на узком экране рядов больше). */
function Collapsible({ maxHeight, label, children }) {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const innerRef = useRef(null);

  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    // +40 — не показываем кнопку ради пары пикселей (полупустой ряд)
    const check = () => setOverflowing(el.scrollHeight > maxHeight + 40);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [maxHeight]);

  const collapsed = !expanded && overflowing;
  return (
    <>
      <div
        className={`rb-up__collapse ${collapsed ? "is-collapsed" : ""}`}
        style={collapsed ? { maxHeight } : undefined}
      >
        <div ref={innerRef}>{children}</div>
      </div>
      {overflowing && (
        <button
          type="button"
          className="rb-up__show-more"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Свернуть" : label}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2.5"
               strokeLinecap="round" strokeLinejoin="round"
               style={{ transform: expanded ? "rotate(180deg)" : "none" }}>
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      )}
    </>
  );
}

/* Карточка достижения Рублокса: {key,title,description,icon,count,value,received}.
 * icon — имя CabIcon (inline SVG). Прогресс уже посчитан бэком. */
function AchievCard({ ach, onClick }) {
  const pct = ach.received ? 100
    : Math.max(0, Math.min(100, Math.round((ach.value / ach.count) * 100)));
  return (
    <button
      type="button"
      className={`rb-up__achiev ${ach.received ? "is-received" : "is-locked"}`}
      onClick={onClick}
      title={ach.received ? "Получено" : "Нажми, чтобы увидеть прогресс"}
    >
      {/* вертикальная плитка: иконка сверху, название, прогресс.
          Описание — в модалке (клик), плитки остаются компактными. */}
      <div className={`rb-up__achiev-icon ${ach.received ? "" : "is-gray"}`}>
        <AchievIcon achKey={ach.key} size={38} />
      </div>
      <div className="rb-up__achiev-name">{ach.title}</div>
      {/* прогресс-полоска: получено = зелёная 100%, иначе синяя pct% */}
      <div className="rb-up__achiev-bar">
        <div
          className={`rb-up__achiev-bar-fill ${ach.received ? "is-done" : ""}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="rb-up__achiev-count">
        {ach.received ? "Получено" : `${ach.value} / ${ach.count}`}
      </div>
    </button>
  );
}

/** Модалка прогресса достижения Рублокса. */
function AchievModal({ ach, onClose }) {
  const pct = ach.received ? 100
    : Math.max(0, Math.min(100, Math.round((ach.value / ach.count) * 100)));
  const left = Math.max(0, ach.count - ach.value);
  return (
    <div className="rb-up__modal-overlay" onClick={onClose}>
      <div className="rb-up__modal" onClick={(e) => e.stopPropagation()}>
        <div className="rb-up__modal-head">
          <div className="rb-up__modal-title">
            <CabIcon name="medal" size={18} />
            Достижение
          </div>
          <button type="button" className="rb-up__modal-close" onClick={onClose} aria-label="Закрыть">
            <CabIcon name="close" size={16} />
          </button>
        </div>
        <div className="rb-up__modal-body rb-up__achiev-modal">
          <div className={`rb-up__achiev-modal-badge ${ach.received ? "" : "is-locked"}`}>
            <AchievIcon achKey={ach.key} size={60} />
          </div>
          <div className="rb-up__achiev-modal-name">{ach.title}</div>
          {ach.description && (
            <div className="rb-up__achiev-modal-desc">{ach.description}</div>
          )}

          {ach.received ? (
            <div className="rb-up__achiev-modal-status is-done">
              <CabIcon name="check" size={15} />
              Получено!
            </div>
          ) : (
            <>
              <div className="rb-up__achiev-bar rb-up__achiev-bar--big">
                <div className="rb-up__achiev-bar-fill" style={{ width: `${pct}%` }} />
              </div>
              <div className="rb-up__achiev-modal-progress">
                Прогресс: {ach.value} из {ach.count}
                {left > 0 ? ` — осталось ${left}` : ""}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* Карточка медали Рублокса: {key,title,description,tier,event,count,value,
 * received,earned_at,meta}. Плитка в стиле ачивок, иконка — MedalIcon
 * (рисованная медаль с цветом тира: бронза/серебро/золото/ивент). */
function MedalCard({ medal, onClick }) {
  const pct = medal.received ? 100
    : Math.max(0, Math.min(100, Math.round((medal.value / medal.count) * 100)));
  return (
    <button
      type="button"
      className={`rb-up__achiev rb-up__medal-tile ${medal.received ? "is-received" : "is-locked"}`}
      onClick={onClick}
      title={medal.received ? "Получена" : "Нажми, чтобы узнать, как получить"}
    >
      <div className={`rb-up__achiev-icon rb-up__medal-tile-icon ${medal.received ? "" : "is-gray"}`}>
        <MedalIcon medalKey={medal.key} tier={medal.tier} size={46} />
      </div>
      <div className="rb-up__achiev-name">{medal.title}</div>
      {medal.event ? (
        <div className="rb-up__achiev-count">
          {medal.received ? "Получена" : "Выдаётся на ивентах"}
        </div>
      ) : (
        <>
          <div className="rb-up__achiev-bar">
            <div
              className={`rb-up__achiev-bar-fill ${medal.received ? "is-done" : ""}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="rb-up__achiev-count">
            {medal.received ? "Получена" : `${medal.value} / ${medal.count}`}
          </div>
        </>
      )}
    </button>
  );
}

/** Модалка медали: описание, прогресс, дата получения и подпись
 * («за игру …») — медаль остаётся навсегда, даже если топ перебили. */
function MedalModal({ medal, onClose }) {
  const pct = medal.received ? 100
    : Math.max(0, Math.min(100, Math.round((medal.value / medal.count) * 100)));
  const left = Math.max(0, medal.count - medal.value);
  let earnedStr = "";
  if (medal.earned_at) {
    try {
      earnedStr = new Date(medal.earned_at).toLocaleDateString("ru-RU");
    } catch {}
  }
  return (
    <div className="rb-up__modal-overlay" onClick={onClose}>
      <div className="rb-up__modal" onClick={(e) => e.stopPropagation()}>
        <div className="rb-up__modal-head">
          <div className="rb-up__modal-title">
            <CabIcon name="trophy" size={18} />
            Медаль
          </div>
          <button type="button" className="rb-up__modal-close" onClick={onClose} aria-label="Закрыть">
            <CabIcon name="close" size={16} />
          </button>
        </div>
        <div className="rb-up__modal-body rb-up__achiev-modal">
          <div className={`rb-up__achiev-modal-badge ${medal.received ? "" : "is-locked"}`}>
            <MedalIcon medalKey={medal.key} tier={medal.tier} size={72} />
          </div>
          <div className="rb-up__achiev-modal-name">{medal.title}</div>
          {medal.description && (
            <div className="rb-up__achiev-modal-desc">{medal.description}</div>
          )}

          {medal.received ? (
            <>
              <div className="rb-up__achiev-modal-status is-done">
                <CabIcon name="check" size={15} />
                Получена{earnedStr ? ` ${earnedStr}` : ""}!
              </div>
              {medal.meta && (
                <div className="rb-up__medal-meta">{medal.meta}</div>
              )}
            </>
          ) : medal.event ? (
            <div className="rb-up__achiev-modal-progress">
              Такую медаль вручают организаторы на ивентах Рублокса.
            </div>
          ) : (
            <>
              <div className="rb-up__achiev-bar rb-up__achiev-bar--big">
                <div className="rb-up__achiev-bar-fill" style={{ width: `${pct}%` }} />
              </div>
              <div className="rb-up__achiev-modal-progress">
                Прогресс: {medal.value} из {medal.count}
                {left > 0 ? ` — осталось ${left}` : ""}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Модалка «Бейджи у имени» ───────────────────────────────────
 * Бейджи зарабатываются автоматически (бэк Cosmetics.py BADGES),
 * юзер выбирает до 3, они рисуются чипами у имени в шапке. */
function BadgesModal({ onClose, onSaved }) {
  const [data, setData] = useState(null); // {badges, displayed, max}
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    getBadges()
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setSelected(d.displayed.filter((k) => BADGE_DEFS[k]));
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || "Не удалось загрузить бейджи");
      });
    return () => { cancelled = true; };
  }, []);

  const max = data?.max || 3;

  const toggle = (b) => {
    if (!b.earned) return;
    if (!selected.includes(b.key) && selected.length >= max) {
      setError(`Можно показывать не больше ${max} бейджей`);
      return;
    }
    setError("");
    setSelected((prev) => prev.includes(b.key)
      ? prev.filter((k) => k !== b.key)
      : [...prev, b.key]);
  };

  const save = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await putDisplayedBadges(selected);
      if (res && res.ok === false) {
        setError(res.message || "Не получилось сохранить");
        return;
      }
      onSaved(res?.displayed_badges != null ? res.displayed_badges : selected.join(","));
    } catch (e) {
      setError(e?.message || "Не получилось сохранить");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rb-up__modal-overlay" onClick={onClose}>
      <div className="rb-up__modal rb-up__modal--shop" onClick={(e) => e.stopPropagation()}>
        <div className="rb-up__modal-head">
          <div className="rb-up__modal-title">
            <CabIcon name="medal" size={18} />
            Бейджи у имени
          </div>
          <button type="button" className="rb-up__modal-close" onClick={onClose} aria-label="Закрыть">
            <CabIcon name="close" size={16} />
          </button>
        </div>
        <div className="rb-up__modal-body">
          <p className="rb-up__modal-hint">
            Бейджи зарабатываются автоматически. Выбери до {max} — они появятся
            у твоего имени и будут видны всем гостям профиля.
          </p>
          {!data && !error && (
            <div className="rb-feed__loading"><div className="rb-cab-spinner" /></div>
          )}
          {data && (
            <div className="rb-up__badge-list">
              {data.badges.map((b) => {
                const def = BADGE_DEFS[b.key] || { title: b.title, icon: b.icon };
                const checked = selected.includes(b.key);
                return (
                  <button
                    key={b.key}
                    type="button"
                    className={`rb-up__badge-row ${b.earned ? "" : "is-locked"} ${checked ? "is-checked" : ""}`}
                    onClick={() => toggle(b)}
                    title={b.earned ? (checked ? "Убрать из шапки" : "Показывать в шапке") : "Ещё не заработан"}
                  >
                    <span className={`rb-up__badge rb-up__badge--earned rb-up__badge--bk-${b.key}`}>
                      <CabIcon name={def.icon} size={12} />
                      {def.title}
                    </span>
                    <span className="rb-up__badge-row-desc">{b.description}</span>
                    <span className="rb-up__badge-row-state">
                      {b.earned
                        ? (checked ? <CabIcon name="check" size={15} /> : "")
                        : "не заработан"}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {error && <div className="rb-up__modal-error">{error}</div>}

          <div className="rb-up__modal-actions">
            <button
              type="button"
              className="rb-up__modal-btn rb-up__modal-btn--primary"
              disabled={busy || !data}
              onClick={save}
            >
              {busy ? "Сохранение…" : "Сохранить"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Модалка «Оформление» ───────────────────────────────────────
 * Тема шапки (8 градиентов) + порядок секций (вверх/вниз) + скрытие.
 * Сохраняется на бэке (PUT /profile/layout) — видно всем посетителям. */
function LayoutModal({ layout, onClose, onSaved, onPreview }) {
  const [theme, setTheme] = useState(layout.theme);
  const [order, setOrder] = useState(layout.order);
  const [hidden, setHidden] = useState(new Set(layout.hidden));
  const [titleFont, setTitleFont] = useState(layout.titleFont);
  const [titles, setTitles] = useState(layout.titles); // {key: своё название}
  const [panel, setPanel] = useState(layout.panel);    // {border,radius,glass,tint}
  const [widgets, setWidgets] = useState(layout.widgets);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Живой предпросмотр: черновик применяется к странице позади модалки.
  useEffect(() => {
    if (!onPreview) return;
    onPreview({
      theme, order, hidden: [...hidden],
      panel, titleFont, titles, widgets,
    });
  }, [theme, order, hidden, panel, titleFont, titles, widgets, onPreview]);

  const move = (key, dir) => {
    setOrder((prev) => {
      const idx = prev.indexOf(key);
      const to = idx + dir;
      if (idx < 0 || to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[to]] = [next[to], next[idx]];
      return next;
    });
  };

  const toggleHidden = (key) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const setTitle = (key, value) => {
    setTitles((prev) => {
      const next = { ...prev };
      if (value.trim()) next[key] = value;
      else delete next[key];
      return next;
    });
  };

  const setPanelKey = (key, value) =>
    setPanel((prev) => ({ ...prev, [key]: value }));

  const toggleWidget = (key) => {
    setError("");
    setWidgets((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      if (prev.length >= WIDGETS_MAX) return prev;
      return [...prev, key];
    });
  };

  const save = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      // titles: шлём только непустые (бэк прогонит через фильтр текста)
      const cleanTitles = {};
      for (const [k, v] of Object.entries(titles)) {
        if ((v || "").trim()) cleanTitles[k] = v.trim();
      }
      const res = await putProfileLayout({
        theme, order, hidden: [...hidden],
        panel, titleFont, titles: cleanTitles, widgets,
      });
      if (res && res.ok === false) {
        setError(res.message || "Не получилось сохранить");
        return;
      }
      onSaved(res.profile_layout || null);
    } catch (e) {
      setError(e?.message || "Не получилось сохранить");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rb-up__modal-overlay rb-up__modal-overlay--lite" onClick={onClose}>
      <div className="rb-up__modal rb-up__modal--shop" onClick={(e) => e.stopPropagation()}>
        <div className="rb-up__modal-head">
          <div className="rb-up__modal-title">
            <CabIcon name="sparkles" size={18} />
            Оформление профиля
          </div>
          <button type="button" className="rb-up__modal-close" onClick={onClose} aria-label="Закрыть">
            <CabIcon name="close" size={16} />
          </button>
        </div>

        <div className="rb-up__modal-body">
          <p className="rb-up__modal-hint" style={{ marginBottom: 10 }}>
            Всё бесплатно, изменения сразу видны на странице позади.
            Не забудь нажать «Сохранить».
          </p>

          {/* Тема шапки */}
          <div className="rb-up__layout-label">Тема шапки</div>
          <p className="rb-up__modal-hint" style={{ marginBottom: 10 }}>
            Видна, когда нет обложки.
          </p>
          <div className="rb-up__layout-themes">
            {Object.entries(HEADER_THEMES).map(([key, t]) => (
              <button
                key={key}
                type="button"
                className={`rb-up__layout-theme ${theme === key ? "is-active" : ""}`}
                style={{ background: t.bg }}
                onClick={() => setTheme(key)}
                title={t.title}
              >
                {theme === key && <CabIcon name="check" size={16} />}
              </button>
            ))}
          </div>

          {/* Стат-виджеты шапки (В1) */}
          <div className="rb-up__layout-label" style={{ marginTop: 18 }}>
            Счётчики в шапке
          </div>
          <p className="rb-up__modal-hint" style={{ marginBottom: 10 }}>
            Выбери до {WIDGETS_MAX} счётчиков — гости увидят их под именем.
          </p>
          <div className="rb-up__layout-widgets">
            {Object.entries(WIDGET_DEFS).map(([key, def]) => {
              const on = widgets.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  className={`rb-up__layout-widget ${on ? "is-on" : ""}`}
                  style={{ "--w-color": def.color }}
                  onClick={() => toggleWidget(key)}
                  title={on ? "Убрать счётчик" : "Показать счётчик"}
                >
                  <CabIcon name={def.icon} size={14} />
                  {def.label}
                  {on && <CabIcon name="check" size={13} />}
                </button>
              );
            })}
          </div>

          {/* Шрифт заголовков секций (Б5) */}
          <div className="rb-up__layout-label" style={{ marginTop: 18 }}>
            Шрифт заголовков
          </div>
          <div className="rb-up__layout-fonts">
            {Object.entries(TITLE_FONT_TITLES).map(([key, title]) => (
              <button
                key={key}
                type="button"
                className={`rb-up__layout-font ${titleFont === key ? "is-active" : ""}`}
                style={TITLE_FONT_STACKS[key] ? { fontFamily: TITLE_FONT_STACKS[key] } : undefined}
                onClick={() => setTitleFont(key)}
              >
                {title}
              </button>
            ))}
          </div>

          {/* Стиль панелей-секций (Б4) */}
          <div className="rb-up__layout-label" style={{ marginTop: 18 }}>
            Панели секций
          </div>
          <div className="rb-up__layout-fonts">
            {Object.entries(PANEL_BORDER_TITLES).map(([key, title]) => (
              <button
                key={key}
                type="button"
                className={`rb-up__layout-font rb-up__layout-border rb-up__layout-border--${key} ${panel.border === key ? "is-active" : ""}`}
                onClick={() => setPanelKey("border", key)}
              >
                {title}
              </button>
            ))}
          </div>
          <div className="rb-up__bgset" style={{ marginTop: 10 }}>
            <BgSlider label="Скругление углов" value={panel.radius != null ? panel.radius : 18}
                      unit="px" min={0} max={28} step={1}
                      onChange={(v) => setPanelKey("radius", v)} />
            <BgSlider label="Прозрачность панелей" value={panel.glass != null ? panel.glass : 0}
                      unit="%" min={0} max={90} step={5}
                      onChange={(v) => setPanelKey("glass", v)} />
            <div className="rb-up__layout-tint">
              <span className="rb-up__bgset-label">Оттенок панелей</span>
              <input
                type="color"
                value={panel.tint || "#ffffff"}
                onChange={(e) => setPanelKey("tint", e.target.value)}
                title="Свой цвет панелей"
              />
              {(panel.tint || panel.radius != null || panel.glass != null || panel.border !== "none") && (
                <button
                  type="button"
                  className="rb-up__modal-btn rb-up__modal-btn--ghost rb-up__layout-tint-reset"
                  onClick={() => setPanel({ border: "none", radius: null, glass: null, tint: "" })}
                >
                  Сбросить панели
                </button>
              )}
            </div>
          </div>

          {/* Порядок, скрытие и свои названия секций */}
          <div className="rb-up__layout-label" style={{ marginTop: 18 }}>
            Секции
          </div>
          <p className="rb-up__modal-hint" style={{ marginBottom: 10 }}>
            Своё название (например «Мои шедевры»), стрелки меняют порядок,
            глаз скрывает секцию у всех посетителей.
          </p>
          <div className="rb-up__layout-sections">
            <div className="rb-up__layout-row">
              <input
                className="rb-up__layout-row-input"
                value={titles.about || ""}
                maxLength={30}
                placeholder="О себе"
                onChange={(e) => setTitle("about", e.target.value)}
              />
              <span className="rb-up__layout-row-fixed">всегда первая</span>
            </div>
            {order.map((key, idx) => (
              <div key={key} className={`rb-up__layout-row ${hidden.has(key) ? "is-hidden" : ""}`}>
                <input
                  className="rb-up__layout-row-input"
                  value={titles[key] || ""}
                  maxLength={30}
                  placeholder={SECTION_TITLES[key]}
                  onChange={(e) => setTitle(key, e.target.value)}
                />
                <div className="rb-up__layout-row-actions">
                  <button type="button" disabled={idx === 0}
                          onClick={() => move(key, -1)} title="Выше" aria-label="Выше">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="2.5"
                         strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="18 15 12 9 6 15" />
                    </svg>
                  </button>
                  <button type="button" disabled={idx === order.length - 1}
                          onClick={() => move(key, 1)} title="Ниже" aria-label="Ниже">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="2.5"
                         strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                  <button type="button" className={hidden.has(key) ? "is-off" : ""}
                          onClick={() => toggleHidden(key)}
                          title={hidden.has(key) ? "Показать секцию" : "Скрыть секцию"}
                          aria-label="Показать/скрыть">
                    <CabIcon name="eye" size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {error && <div className="rb-up__modal-error">{error}</div>}

          <div className="rb-up__modal-actions">
            <button
              type="button"
              className="rb-up__modal-btn rb-up__modal-btn--primary"
              disabled={busy}
              onClick={save}
            >
              {busy ? "Сохранение…" : "Сохранить"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CommentForm({ value, onChange, onSubmit, busy }) {
  return (
    <div className="rb-up__comment-form">
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Напиши пользователю в гостевой книге…"
        maxLength={500}
        rows={2}
      />
      <button
        type="button"
        onClick={onSubmit}
        disabled={busy || !value.trim()}
        className="rb-up__comment-submit"
      >
        {busy ? "Отправка…" : "Отправить"}
      </button>
    </div>
  );
}

function CommentRow({ comment, canDelete, onDelete, navigate }) {
  const author = comment.firstName || comment.username || comment.author || "Пользователь";
  const text = comment.textComent || comment.text || comment.body || "";
  const authorId = comment.userId || comment.idUser || null;
  // Аватар: предпочитаем photo_thumb_b64 (base64 PNG из bulk-API),
  // fallback на photo (относительный путь, через profilePhotoSrc).
  const photo = comment.photo_thumb_b64
    ? `data:image/png;base64,${comment.photo_thumb_b64}`
    : (comment.photo ? profilePhotoSrc(comment.photo) : null);
  const goToAuthor = () => {
    if (authorId && navigate) navigate(`/app/profile/${authorId}`);
  };
  return (
    <div className="rb-up__comment">
      <button
        type="button"
        className="rb-up__comment-avatar rb-up__comment-avatar--link"
        onClick={goToAuthor}
        disabled={!authorId}
        title={authorId ? "Открыть профиль" : ""}
      >
        {photo ? (
          <img src={photo} alt={author} onError={(e) => { e.currentTarget.style.display = "none"; }} />
        ) : (
          <span style={{ background: avatarInitialColor(author) }}>
            {String(author).charAt(0).toUpperCase()}
          </span>
        )}
      </button>
      <div className="rb-up__comment-body">
        <div className="rb-up__comment-head">
          <button
            type="button"
            className="rb-up__comment-author"
            onClick={goToAuthor}
            disabled={!authorId}
          >
            {author}
          </button>
          {canDelete && (
            <button
              type="button"
              className="rb-up__comment-del"
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm("Удалить этот комментарий?")) onDelete();
              }}
              title="Удалить комментарий"
              aria-label="Удалить"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2"
                   strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                <line x1="10" y1="11" x2="10" y2="17" />
                <line x1="14" y1="11" x2="14" y2="17" />
              </svg>
            </button>
          )}
        </div>
        <div className="rb-up__comment-text">{text}</div>
      </div>
    </div>
  );
}

/* ── склонения ─────────────────────────────────────────────────── */
function pluralGames(n) {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return "игра";
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return "игры";
  return "игр";
}
function pluralAch(n) {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return "достижение";
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return "достижения";
  return "достижений";
}
function pluralMedals(n) {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return "медаль";
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return "медали";
  return "медалей";
}
function pluralGifts(n) {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return "подарок";
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return "подарка";
  return "подарков";
}
function pluralComments(n) {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return "комментарий";
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return "комментария";
  return "комментариев";
}
